/**
 * Company Settings Modal
 * L0-CMD-2026-0120-011 - White-label branding configuration
 *
 * Allows company admins/owners to:
 * - Update company name
 * - Upload company logo
 * - Configure brand colors
 */

import React, { useState, useRef, useEffect } from 'react';

interface Company {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  role: string;
}

interface CompanySettingsProps {
  company: Company;
  onClose: () => void;
  onUpdate: (company: Company) => void;
}

const CompanySettings: React.FC<CompanySettingsProps> = ({ company, onClose, onUpdate }) => {
  const [name, setName] = useState(company.name);
  const [primaryColor, setPrimaryColor] = useState(company.primary_color || '#2563eb');
  const [secondaryColor, setSecondaryColor] = useState(company.secondary_color || '#1e40af');
  const [logoPreview, setLogoPreview] = useState<string | null>(company.logo_url);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please use PNG, JPG, SVG, or WebP');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('File too large. Max 2MB');
      return;
    }

    setError(null);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    const token = localStorage.getItem('access_token');

    try {
      // Upload logo if changed
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);

        const logoResponse = await fetch('/api/companies/current/logo', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!logoResponse.ok) {
          const err = await logoResponse.json();
          throw new Error(err.error || 'Failed to upload logo');
        }
      }

      // Update branding
      const response = await fetch('/api/companies/current/branding', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          primary_color: primaryColor,
          secondary_color: secondaryColor
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to update settings');
      }

      const updated = await response.json();
      onUpdate(updated);
      onClose();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content company-settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Company Settings</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              {error}
            </div>
          )}

          {/* Logo Upload */}
          <div className="setting-group">
            <label>Company Logo</label>
            <div className="logo-upload">
              <div
                className="logo-preview"
                style={{ backgroundColor: primaryColor }}
                onClick={() => fileInputRef.current?.click()}
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" />
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                )}
              </div>
              <div className="logo-info">
                <button
                  type="button"
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload Logo
                </button>
                <span className="upload-hint">PNG, JPG, SVG, or WebP. Max 2MB.</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleLogoSelect}
                hidden
              />
            </div>
          </div>

          {/* Company Name */}
          <div className="setting-group">
            <label htmlFor="company-name">Company Name</label>
            <input
              id="company-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your Company Name"
            />
          </div>

          {/* Brand Colors */}
          <div className="setting-group">
            <label>Brand Colors</label>
            <div className="color-pickers">
              <div className="color-picker">
                <span className="color-label">Primary</span>
                <div className="color-input">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={e => setPrimaryColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={e => setPrimaryColor(e.target.value)}
                    placeholder="#2563eb"
                  />
                </div>
              </div>
              <div className="color-picker">
                <span className="color-label">Secondary</span>
                <div className="color-input">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={e => setSecondaryColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={e => setSecondaryColor(e.target.value)}
                    placeholder="#1e40af"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="setting-group">
            <label>Preview</label>
            <div
              className="brand-preview"
              style={{
                borderColor: primaryColor,
                background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}15 100%)`
              }}
            >
              <div className="preview-header" style={{ backgroundColor: primaryColor }}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Preview" className="preview-logo" />
                ) : (
                  <span className="preview-initial">{name.charAt(0).toUpperCase()}</span>
                )}
                <span className="preview-name">{name}</span>
              </div>
              <div className="preview-body">
                <p>This is how your portal header will appear to your team.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !name.trim()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          backdrop-filter: blur(4px);
        }

        .company-settings-modal {
          background: var(--bg-secondary, #1e293b);
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border-color, #334155);
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          color: var(--text-primary, #f8fafc);
        }

        .close-btn {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }

        .close-btn:hover {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary);
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .error-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #f87171;
          font-size: 14px;
          margin-bottom: 20px;
        }

        .setting-group {
          margin-bottom: 24px;
        }

        .setting-group label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .setting-group input[type="text"] {
          width: 100%;
          padding: 10px 12px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #475569);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
        }

        .setting-group input[type="text"]:focus {
          outline: none;
          border-color: var(--primary, #2563eb);
        }

        .logo-upload {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .logo-preview {
          width: 80px;
          height: 80px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          overflow: hidden;
          color: white;
          transition: opacity 0.2s;
        }

        .logo-preview:hover {
          opacity: 0.8;
        }

        .logo-preview img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .logo-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .upload-btn {
          padding: 8px 16px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #475569);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .upload-btn:hover {
          background: var(--bg-secondary);
        }

        .upload-hint {
          font-size: 12px;
          color: var(--text-muted, #64748b);
        }

        .color-pickers {
          display: flex;
          gap: 16px;
        }

        .color-picker {
          flex: 1;
        }

        .color-label {
          display: block;
          font-size: 12px;
          color: var(--text-muted, #64748b);
          margin-bottom: 6px;
        }

        .color-input {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #475569);
          border-radius: 6px;
          padding: 4px 8px;
        }

        .color-input input[type="color"] {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          padding: 0;
        }

        .color-input input[type="text"] {
          flex: 1;
          background: none;
          border: none;
          color: var(--text-primary);
          font-size: 13px;
          font-family: monospace;
        }

        .color-input input[type="text"]:focus {
          outline: none;
        }

        .brand-preview {
          border: 2px solid;
          border-radius: 8px;
          overflow: hidden;
        }

        .preview-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          color: white;
        }

        .preview-logo {
          width: 32px;
          height: 32px;
          object-fit: contain;
        }

        .preview-initial {
          width: 32px;
          height: 32px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
        }

        .preview-name {
          font-weight: 600;
        }

        .preview-body {
          padding: 16px;
        }

        .preview-body p {
          margin: 0;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--border-color, #334155);
        }

        .btn-secondary {
          padding: 10px 20px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #475569);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-secondary:hover {
          background: var(--bg-secondary);
        }

        .btn-primary {
          padding: 10px 20px;
          background: var(--primary, #2563eb);
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default CompanySettings;
